import { Input } from "@/components/core/forms/input.tsx";
import { Slot } from "@radix-ui/react-slot";
import { IconAsteriskSimple } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import omit from "lodash/omit";
import React from "react";
import { Controller, type ControllerProps, type FieldPath, type FieldValues, FormProvider, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";

const Form = FormProvider;

type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
	name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

type FormItemContextValue = {
	id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState, formState, control } = useFormContext();

	const fieldState = getFieldState(fieldContext.name, formState);

	if (!fieldContext) {
		throw new Error("useFormField should be used within <FormField>");
	}

	const { id } = itemContext;

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState,
	};
};

/**
 * Form Field
 */
const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
	...props
}: ControllerProps<TFieldValues, TName>) => {
	return (
		<FormFieldContext.Provider value={{ name: props.name }}>
			<Controller {...props} />
		</FormFieldContext.Provider>
	);
};

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(({ ...props }, ref) => {
	const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

	return (
		<Slot
			ref={ref}
			id={formItemId}
			aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
			aria-invalid={!!error}
			{...props}
		/>
	);
});
FormControl.displayName = "FormControl";

/**
 * FormItem
 */
const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	const id = React.useId();

	return (
		<FormItemContext.Provider value={{ id }}>
			<div ref={ref} className={clsx("flex flex-col w-full gap-1", className)} {...props} />
		</FormItemContext.Provider>
	);
});
FormItem.displayName = "FormItem";

/**
 * FormLabel
 */
const FormLabel = React.forwardRef<
	HTMLLabelElement,
	React.HTMLAttributes<HTMLLabelElement> & {
		required?: boolean;
	}
>(({ className, ...props }, ref) => {
	const { error, formItemId } = useFormField();
	const { required = false } = props;
	const labelProps = omit(props, ["required"]);

	return (
		<div className="flex flex-row items-center gap-1">
			<label ref={ref} htmlFor={formItemId} className={clsx("text-base", !error && "text-foreground", error && "text-danger-600", className)} {...labelProps}>
				{labelProps?.children} {/* Ensures label has content */}
			</label>
			{required && (
				<span className="text-danger-600">
					<IconAsteriskSimple size={10} stroke={3} />
				</span>
			)}
		</div>
	);
});
FormLabel.displayName = "FormLabel";

/**
 * FormMessage
 */
const FormMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, children, ...props }, ref) => {
	const { error, formMessageId } = useFormField();
	const body = error ? String(error?.message) : children;

	if (!body) {
		return null;
	}

	return (
		<p ref={ref} id={formMessageId} className={clsx("text-sm text-danger-600", className)} {...props}>
			{body}
		</p>
	);
});
FormMessage.displayName = "FormMessage";

/**
 * FormDescription
 */
const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => {
	const { formDescriptionId } = useFormField();

	return <p ref={ref} id={formDescriptionId} className={clsx("text-sm text-muted-foreground", className)} {...props} />;
});
FormDescription.displayName = "FormDescription";

/**
 * FormGroup
 */
interface FormGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	inputSize?: "default" | "sm" | "md" | "lg";
	iconPosition?: "left" | "right";
}

const inputStyles = cva("border-border border focus:border-focus-border focus:ring-2 ring-offset-0 focus:ring-focus-ring/30 outline-0 outline-none", {
	variants: {
		size: {
			default: "min-h-[38px] max-h-[38px] h-[38px] px-3 text-base rounded-md",
			sm: "min-h-[28px] max-h-[28px] h-[28px] px-2 text-sm rounded-md",
			md: "min-h-[38px] max-h-[38px] h-[38px] px-3 text-base rounded-md",
			lg: "min-h-[48px] max-h-[48px] h-[48px] px-6 text-lg  rounded-lg",
		},
		iconPosition: {
			left: "pl-8",
			right: "pr-8",
		},
	},
	defaultVariants: {
		size: "default",
	},
});

const FormGroup = React.forwardRef<HTMLDivElement, FormGroupProps>(({ inputSize = "md", className, children, iconPosition = "left", ...props }, ref) => {
	const hasIcon = React.Children.toArray(children).some((child) => React.isValidElement(child) && child.type === FormIcon);

	const input = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			if (child.type === Input || child.type === NumericFormat) {
				return React.cloneElement(child as React.ReactElement<{ className?: string }>, {
					className: clsx(
						inputStyles({
							size: inputSize,
							iconPosition: hasIcon ? iconPosition : undefined,
						}),
						child.props.className,
					),
				});
			}

			if (child.type === FormIcon) {
				return React.cloneElement(child as React.ReactElement<{ className?: string }>, {
					className: clsx("absolute inline-flex", iconPosition === "left" ? "left-3" : "right-3", child.props.className),
				});
			}

			if (child.type === FormPrepend) {
				return React.cloneElement(child as React.ReactElement<{ className?: string }>, {
					className: clsx("inline-flex", inputSize === "sm" && "px-2", inputSize === "md" && "px-3", inputSize === "lg" && "px-6", child.props.className),
				});
			}
		}

		return child;
	});

	return (
		<div ref={ref} className={clsx("relative flex flex-row w-full", className)} {...props}>
			<div className="flex flex-row items-start grow shrink w-full">{input}</div>
		</div>
	);
});
FormGroup.displayName = "FormGroup";

/**
 * FormIcon
 * The position of the FormIcon is automatically handled by FormGroup.
 */
const FormIcon = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={clsx("absolute top-1/2 -translate-y-1/2 inline-flex text-muted-foreground", className)} {...props} />;
});
FormIcon.displayName = "FormIcon";

const FormPrepend = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={clsx(
				"inline-flex justify-center items-center w-auto h-full border border-r-0 border-border rounded-l-md bg-slate-50 text-muted-foreground",
				className,
			)}
			{...props}
		/>
	);
});
FormPrepend.displayName = "FormPrepend";

export { useFormField, Form, FormControl, FormDescription, FormItem, FormIcon, FormPrepend, FormMessage, FormField, FormGroup, FormLabel };
